import { type ReactNode } from 'react';
import { Surface } from '@/components/ui';

/**
 * Stat card primitive used across the analytics surfaces (Overview,
 * /app/analytics, /app/links/<id>). The point of this component is
 * alignment: every card on a row has the same internal structure
 *
 *     [labelIcon] LABEL                  ← row 1 (top)
 *     <big value>                        ← row 2
 *     ...flex spacer...
 *     [badge] [meta]                     ← row 3 (bottom-anchored)
 *
 * Because the bottom row uses `mt-auto` and the Surface is `h-full`,
 * the bottom row sits flush against the bottom edge of EVERY card
 * regardless of how much content each card carries. CSS grid stretches
 * all cells to match the tallest card, and the bottom anchor lines
 * them up visually.
 *
 * Cards that don't have a badge or meta line just omit them — the
 * flex spacer expands to fill the unused space, keeping the value
 * top-anchored.
 */
export interface MetricCardProps {
  /** Top-row uppercase label. */
  label: string;
  /** Optional icon left of the label (pulsing dot, etc.). */
  labelIcon?: ReactNode;
  /** Big primary number or short text. */
  value: ReactNode;
  /** Bottom-row badge — small colored chip. Optional. */
  badge?: ReactNode;
  /** Bottom-row context text — sits next to the badge or alone. */
  meta?: ReactNode;
}

export function MetricCard({ label, labelIcon, value, badge, meta }: MetricCardProps) {
  return (
    <Surface tone="raised" className="flex h-full flex-col p-5">
      <div className="flex items-center gap-2">
        {labelIcon}
        <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--color-on-surface-variant)]">
          {label}
        </div>
      </div>
      <div className="mt-2 text-3xl font-semibold tabular-nums leading-none">
        {value}
      </div>
      {(badge || meta) && (
        <div className="mt-auto flex flex-wrap items-center gap-2 pt-3 text-xs text-[var(--color-on-surface-variant)]">
          {badge}
          {meta}
        </div>
      )}
    </Surface>
  );
}
